import React, { useState, useEffect, useCallback, useMemo } from 'react';
import QuestionCard from '../components/questions/QuestionCard';
import GameProgressPanel from '../components/game/GameProgressPanel';
import GameSessionReviewPanel from '../components/game/GameSessionReviewPanel';
import { ArrowLeftIcon, ClockIcon, HeartIcon } from '@heroicons/react/24/outline';
import { HeartIcon as HeartSolidIcon } from '@heroicons/react/24/solid';
import { Link } from 'react-router-dom';
import { questionAPI, gameSessionAPI } from '../services/api';
import type { GameSessionReview } from '../services/api';
import FreeTierLimitModal from '../components/modals/FreeTierLimitModal';
import { useSubscriptionStatus } from '../hooks/useSubscriptionStatus';
import { isFreeTierLimitError } from '../utils/freeTier';
import { QuestionApiModel } from '../types/questions';
import { getUserMessage } from '../utils/errors';

interface QuestionAttempt {
  question_number: number;
  is_correct: boolean;
  difficulty_rating: number | null;
  selected_answer: string;
  correct_answer: string;
}

interface RushDuration {
  minutes: number;
  label: string;
  description: string;
}

const RUSH_DURATIONS: RushDuration[] = [
  { minutes: 5, label: '5 Minutes', description: 'Quick sprint challenge' },
  { minutes: 10, label: '10 Minutes', description: 'Standard rush mode' },
  { minutes: 20, label: '20 Minutes', description: 'Extended challenge' },
  { minutes: 30, label: '30 Minutes', description: 'Endurance mode' }
];

const Rush: React.FC = () => {
  const [question, setQuestion] = useState<QuestionApiModel | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submittingAnswer, setSubmittingAnswer] = useState(false);
  const [answerStartTime, setAnswerStartTime] = useState<number>(Date.now());

  // Game setup state
  const [selectedDuration, setSelectedDuration] = useState<number | null>(null);
  const [gameSessionId, setGameSessionId] = useState<string | null>(null);
  const [completedSessionId, setCompletedSessionId] = useState<string | null>(null);
  const [reviewData, setReviewData] = useState<GameSessionReview | null>(null);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [reviewError, setReviewError] = useState<string | null>(null);

  // Game state
  const [lives, setLives] = useState(3);
  const [score, setScore] = useState(0);
  const [timeLeft, setTimeLeft] = useState(0);
  const [gameStarted, setGameStarted] = useState(false);
  const [gameOver, setGameOver] = useState(false);
  const [gameStartTime, setGameStartTime] = useState<number>(0);
  const [currentQuestionNumber, setCurrentQuestionNumber] = useState(1);
  const [attemptHistory, setAttemptHistory] = useState<QuestionAttempt[]>([]);
  const [lastAnswerResult, setLastAnswerResult] = useState<{ isCorrect: boolean; selected: string; correctAnswer: string } | null>(null);
  const { subscriptionStatus, isLoading: isSubscriptionLoading, refresh: refreshSubscriptionStatus } = useSubscriptionStatus();
  const [showLimitModal, setShowLimitModal] = useState(false);
  const rushUsage = subscriptionStatus?.free_tier_usage;
  const isFreePlan = subscriptionStatus?.is_on_free_plan ?? false;
  const rushLimitReached = useMemo(() => {
    if (!isFreePlan || !rushUsage) {
      return false;
    }
    return rushUsage.rush_sessions_used >= rushUsage.rush_sessions_limit;
  }, [isFreePlan, rushUsage]);
  const rushLimitBlocking = useMemo(
    () => rushLimitReached && !gameSessionId && !gameStarted,
    [rushLimitReached, gameSessionId, gameStarted]
  );
  const isPageLoading = (loading || isSubscriptionLoading) && gameStarted && !question && !rushLimitBlocking;
  const rushUsageLabel = rushUsage
    ? `${rushUsage.rush_sessions_used}/${rushUsage.rush_sessions_limit} runs`
    : null;
  const rushResetLabel = useMemo(() => {
    if (!rushUsage) return null;
    try {
      const resetDate = new Date(rushUsage.resets_at);
      return `Resets ${resetDate.toLocaleTimeString(undefined, {
        hour: 'numeric',
        minute: '2-digit',
      })}`;
    } catch {
      return `Resets ${rushUsage.resets_on}`;
    }
  }, [rushUsage]);

  const fetchNextQuestion = useCallback(async (questionNumber: number, duration?: number) => {
    const durationToUse = duration || selectedDuration;

    if (!durationToUse) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setLastAnswerResult(null);

      const data = await questionAPI.getRushQuestion(questionNumber, durationToUse);
      setQuestion(data);
      setAnswerStartTime(Date.now());
    } catch (err) {
      console.error('🎯 Rush: Error fetching question:', err);
      if (isFreeTierLimitError(err)) {
        setShowLimitModal(true);
        await refreshSubscriptionStatus().catch(() => {});
      } else {
        setError(getUserMessage(err, 'Unable to load a Rush question. Please try again.'));
      }
    } finally {
      setLoading(false);
    }
  }, [refreshSubscriptionStatus, selectedDuration]);

  const updateTimer = useCallback(() => {
    if (!gameStarted || gameOver || !selectedDuration) return;

    const elapsed = Date.now() - gameStartTime;
    const totalTime = selectedDuration * 60 * 1000; // Convert minutes to milliseconds
    const remaining = Math.max(0, totalTime - elapsed);
    setTimeLeft(remaining);

    if (remaining <= 0) {
      setGameOver(true);
      setGameStarted(false);
      const sessionId = gameSessionId;
      // Mark session as completed
      if (sessionId) {
        gameSessionAPI.updateProgress(sessionId, {
          questions_answered: currentQuestionNumber - 1,
          correct_answers: score,
          lives_remaining: lives,
          is_completed: true
        });
        setCompletedSessionId(sessionId);
        setGameSessionId(null);
      }
    }
  }, [gameStarted, gameOver, gameStartTime, selectedDuration, currentQuestionNumber, score, lives, gameSessionId]);

  useEffect(() => {
    if (gameStarted && !gameOver) {
      const interval = setInterval(updateTimer, 100);
      return () => clearInterval(interval);
    }
  }, [gameStarted, gameOver, updateTimer]);

  useEffect(() => {
    if (gameOver && completedSessionId) {
      setReviewLoading(true);
      setReviewError(null);
      gameSessionAPI.getSessionReview(completedSessionId)
        .then((data) => {
          setReviewData(data);
        })
        .catch((err) => {
          console.error('Failed to load rush session review:', err);
          const message = getUserMessage(err, 'Failed to load review.');
          setReviewError(message);
        })
        .finally(() => setReviewLoading(false));
    }
  }, [gameOver, completedSessionId]);

  const startGame = async (duration: number) => {
    if (isFreePlan && duration !== 5) {
      setShowLimitModal(true);
      return;
    }

    if (rushLimitBlocking) {
      setShowLimitModal(true);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setSelectedDuration(duration);

      const session = await gameSessionAPI.startSession('rush', duration);
      setGameSessionId(session.id);

      setLives(3);
      setScore(0);
      setCurrentQuestionNumber(1);
      setAttemptHistory([]);
      setLastAnswerResult(null);
      setGameOver(false);
      setGameStarted(true);
      setCompletedSessionId(null);
      setReviewData(null);
      setReviewError(null);
      setGameStartTime(Date.now());
      setTimeLeft(duration * 60 * 1000);

      await refreshSubscriptionStatus().catch(() => {});
      await fetchNextQuestion(1, duration);
    } catch (err) {
      console.error('🎮 Rush: Error starting game:', err);
      if (isFreeTierLimitError(err)) {
        setShowLimitModal(true);
        await refreshSubscriptionStatus().catch(() => {});
      } else {
        setError(getUserMessage(err, 'Failed to start game'));
      }
      setLoading(false);
    }
  };

  const handleAnswer = async (selectedAnswerValue: string, timeSpentSeconds?: number) => {
    if (!question || submittingAnswer || gameOver || !gameSessionId) return;

    setSubmittingAnswer(true);
    try {
      const resolvedTimeSpentSeconds =
        typeof timeSpentSeconds === 'number'
          ? timeSpentSeconds
          : Math.floor((Date.now() - answerStartTime) / 1000);

      const result = await questionAPI.submitAnswer({
        question_id: question.id,
        selected_answer: selectedAnswerValue,
        time_spent_seconds: resolvedTimeSpentSeconds,
        game_mode: 'rush',
        session_id: gameSessionId
      });

      if (result.question_progress) {
        setQuestion((prev) =>
          prev
            ? {
                ...prev,
                progress: result.question_progress,
              }
            : prev
        );
      }

      const isCorrect = result.is_correct;

      // Add to attempt history
      const attempt: QuestionAttempt = {
        question_number: currentQuestionNumber,
        is_correct: isCorrect,
        difficulty_rating: question.difficulty_rating,
        selected_answer: selectedAnswerValue,
        correct_answer: result.correct_answer
      };
      setAttemptHistory(prev => [...prev, attempt]);

      // Show immediate feedback
      setLastAnswerResult({
        isCorrect: isCorrect,
        selected: selectedAnswerValue,
        correctAnswer: result.correct_answer
      });

      // Update game state
      if (isCorrect) {
        const newScore = score + 1;
        setScore(newScore);

        // Update session progress
        await gameSessionAPI.updateProgress(gameSessionId, {
          questions_answered: currentQuestionNumber,
          correct_answers: newScore,
          lives_remaining: lives
        });

        // Load next question after short delay for feedback
        setTimeout(async () => {
          setCurrentQuestionNumber(currentQuestionNumber + 1);
          await fetchNextQuestion(currentQuestionNumber + 1);
        }, 500);
      } else {
        const newLives = lives - 1;
        setLives(newLives);

        if (newLives <= 0) {
          const sessionId = gameSessionId;
          setGameOver(true);
          // Mark session as completed
          if (sessionId) {
            await gameSessionAPI.updateProgress(sessionId, {
              questions_answered: currentQuestionNumber,
              correct_answers: score,
              lives_remaining: 0,
              is_completed: true
            });
            setCompletedSessionId(sessionId);
          }
          setGameSessionId(null);
          setGameStarted(false);
        } else {
          // Update session progress and continue
          await gameSessionAPI.updateProgress(gameSessionId, {
            questions_answered: currentQuestionNumber,
            correct_answers: score,
            lives_remaining: newLives
          });

          // Load next question after short delay for feedback
          setTimeout(async () => {
            setCurrentQuestionNumber(currentQuestionNumber + 1);
            await fetchNextQuestion(currentQuestionNumber + 1);
          }, 500);
        }
      }
    } catch (err) {
      console.error('Failed to submit answer:', err);
      if (isFreeTierLimitError(err)) {
        setShowLimitModal(true);
        await refreshSubscriptionStatus().catch(() => {});
      } else {
        setError('Failed to submit answer. Please try again.');
      }
    } finally {
      setSubmittingAnswer(false);
    }
  };

  const restartGame = () => {
    if (rushLimitBlocking) {
      setShowLimitModal(true);
      return;
    }
    setSelectedDuration(null);
    setGameSessionId(null);
    setLives(3);
    setScore(0);
    setTimeLeft(0);
    setCurrentQuestionNumber(1);
    setAttemptHistory([]);
    setGameStarted(false);
    setGameOver(false);
    setLastAnswerResult(null);
    setLoading(false);
    setCompletedSessionId(null);
    setReviewData(null);
    setReviewError(null);
  };

  const formatTime = (milliseconds: number) => {
    const minutes = Math.floor(milliseconds / 60000);
    const seconds = Math.floor((milliseconds % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const renderLives = () => {
    return Array.from({ length: 3 }, (_, i) => (
      <div key={i} className="inline-flex">
        {i < lives ? (
          <HeartSolidIcon className="h-6 w-6 text-red-500" />
        ) : (
          <HeartIcon className="h-6 w-6 text-gray-400" />
        )}
      </div>
    ));
  };

  const getTimeColor = () => {
    if (!selectedDuration) return 'text-gray-600 dark:text-gray-400';
    const totalTime = selectedDuration * 60 * 1000;
    const percentage = timeLeft / totalTime;
    if (percentage > 0.5) return 'text-green-600 dark:text-green-400';
    if (percentage > 0.2) return 'text-yellow-600 dark:text-yellow-400';
    return 'text-red-600 dark:text-red-400';
  };

  if (!gameStarted && !gameOver && selectedDuration === null) {
    return (
      <div>
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
          <div className="max-w-2xl mx-auto text-center">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8">
              {subscriptionStatus?.is_on_free_plan && rushUsage && (
                <div
                  className={`mb-6 rounded-lg border p-4 ${
                    rushLimitBlocking
                      ? 'border-subject-300 bg-subject-50 text-subject-800 dark:border-subject-800/40 dark:bg-subject-900/20 dark:text-subject-200'
                      : 'border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-800/40 dark:bg-blue-900/20 dark:text-blue-200'
                  }`}
                >
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm font-semibold">Free Plan Rush Access</p>
                      <p className="text-sm">
                  {rushLimitBlocking
                    ? 'You have used today’s free 5-minute Rush run for today. Upgrade to keep racing the clock.'
                    : 'Free members can launch one 5-minute Rush run per day. Ready for your challenge?'}
                      </p>
                    </div>
                    <div className="text-sm sm:text-right">
                      {rushUsageLabel && <p className="font-semibold">{rushUsageLabel}</p>}
                      {rushResetLabel && <p className="text-xs opacity-80">{rushResetLabel}</p>}
                    </div>
                  </div>
                </div>
              )}

              <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 bg-blue-100 dark:bg-blue-900/20 rounded-full">
                <ClockIcon className="w-8 h-8 text-blue-600 dark:text-blue-400" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Rush Mode</h2>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                Choose your challenge duration and race against time to answer as many questions as possible!
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                {RUSH_DURATIONS.map((duration) => {
                  const isProOnly = isFreePlan && duration.minutes !== 5;
                  const isLocked = isProOnly || rushLimitBlocking;
                  const disabled = loading;

                  return (
                    <button
                      key={duration.minutes}
                      onClick={() => startGame(duration.minutes)}
                      disabled={disabled}
                      className={`p-4 border-2 rounded-lg transition-colors group ${
                        disabled
                          ? 'border-gray-200 dark:border-gray-700 bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-500 cursor-not-allowed'
                          : 'border-gray-200 dark:border-gray-600 hover:border-subject-500 dark:hover:border-subject-400 hover:bg-subject-50 dark:hover:bg-subject-900/20 cursor-pointer'
                      } ${isLocked ? 'opacity-70 hover:border-gray-300 dark:hover:border-gray-600 cursor-pointer' : ''}`}
                    >
                      <div className="text-center">
                        <div className="text-2xl font-bold text-subject-600 dark:text-subject-400 group-hover:text-subject-700 dark:group-hover:text-subject-300">
                          {duration.label}
                        </div>
                        {isLocked && (
                          <div className="mt-1 inline-flex items-center justify-center px-2 py-0.5 text-xs font-semibold rounded-full bg-subject-100 text-subject-700">
                            {isProOnly ? 'Pro' : 'Used Today'}
                          </div>
                        )}
                        <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">{duration.description}</div>
                      </div>
                    </button>
                  );
                })}
              </div>

              <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 mb-6">
                <h3 className="text-sm font-semibold text-blue-800 dark:text-blue-200 mb-2">Rush Mode Rules</h3>
                <ul className="text-xs text-blue-700 dark:text-blue-300 space-y-1">
                  <li>• 3 lives - lose one for each wrong answer</li>
                  <li>• Progressive difficulty as you advance</li>
                  <li>• Score points for correct answers</li>
                  <li>• Game ends when time runs out or lives are lost</li>
                </ul>
              </div>

              <Link
                to="/dashboard"
                className="block w-full px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-500 transition-colors text-center"
              >
                Back to Dashboard
              </Link>
            </div>
          </div>
        </div>
        <FreeTierLimitModal
          isOpen={showLimitModal}
          onClose={() => setShowLimitModal(false)}
          feature="rush"
          usage={subscriptionStatus?.free_tier_usage}
        />
      </div>
    );
  }

  if (gameOver) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 space-y-10">
          <div className="max-w-md mx-auto text-center">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8">
              <div
                className={`flex items-center justify-center w-16 h-16 mx-auto mb-4 rounded-full ${
                  lives > 0 ? 'bg-blue-100 dark:bg-blue-900/20' : 'bg-red-100 dark:bg-red-900/20'
                }`}
              >
                {lives > 0 ? (
                  <ClockIcon className="w-8 h-8 text-blue-600 dark:text-blue-400" />
                ) : (
                  <svg className="w-8 h-8 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                )}
              </div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                {lives > 0 ? "Time's Up!" : 'Game Over!'}
              </h2>
              <p className="text-gray-600 dark:text-gray-400 mb-1">
                Duration: <span className="font-bold">{selectedDuration} minutes</span>
              </p>
              <p className="text-gray-600 dark:text-gray-400 mb-2">
                Final Score: <span className="font-bold text-subject-600">{score}</span>
              </p>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                {lives > 0
                  ? `You survived the full ${selectedDuration} minutes! Well done!`
                  : 'You ran out of lives! Keep practicing to improve.'}
              </p>
              <div className="space-y-3">
                <button
                  onClick={restartGame}
                  disabled={rushLimitBlocking || loading}
                  className={`w-full px-4 py-2 rounded-lg transition-colors ${
                    rushLimitBlocking || loading
                      ? 'bg-gray-400 text-white cursor-not-allowed'
                      : 'bg-subject-600 text-white hover:bg-subject-500'
                  }`}
                >
                  Play Again
                </button>
                <Link
                  to="/dashboard"
                  className="block w-full px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-500 transition-colors text-center"
                >
                  Back to Dashboard
                </Link>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Review Your Run</h3>
            {reviewLoading && (
              <div className="flex items-center justify-center py-6">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-subject-500"></div>
                <span className="ml-3 text-sm text-gray-600 dark:text-gray-300">Loading review...</span>
              </div>
            )}
            {!reviewLoading && reviewError && (
              <div className="py-4 text-sm text-red-600 dark:text-red-400">{reviewError}</div>
            )}
            {!reviewLoading && !reviewError && reviewData && (
              <GameSessionReviewPanel review={reviewData} />
            )}
            {!reviewLoading && !reviewError && !reviewData && (
              <p className="text-sm text-gray-600 dark:text-gray-400">No attempts recorded for this run.</p>
            )}
          </div>
        </div>
        <FreeTierLimitModal
          isOpen={showLimitModal}
          onClose={() => setShowLimitModal(false)}
          feature="rush"
          usage={subscriptionStatus?.free_tier_usage}
        />
      </div>
    );
  }

  if (isPageLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-subject-500 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-300">Loading question...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6">
            <h2 className="text-lg font-semibold text-red-800 dark:text-red-200 mb-2">
              Error Loading Question
            </h2>
            <p className="text-red-600 dark:text-red-300 mb-4">{error}</p>
            <button
              onClick={() => fetchNextQuestion(currentQuestionNumber)}
              className="px-4 py-2 bg-subject-600 text-white rounded-lg hover:bg-subject-500"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {subscriptionStatus?.is_on_free_plan && rushUsage && (
          <div
            className={`mb-6 rounded-lg border p-4 ${
              rushLimitBlocking
                ? 'border-subject-300 bg-subject-50 text-subject-800 dark:border-subject-800/40 dark:bg-subject-900/20 dark:text-subject-200'
                : 'border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-800/40 dark:bg-blue-900/20 dark:text-blue-200'
            }`}
          >
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="text-sm font-semibold">Free Plan Rush Access</p>
                <p className="text-sm">
                  {rushLimitBlocking
                    ? 'You have used today’s free Rush run. Start a subscription to run again immediately.'
                    : 'You’re currently using your free Rush run for today. Upgrade for unlimited speed runs.'}
                </p>
              </div>
              <div className="text-sm sm:text-right">
                {rushUsageLabel && <p className="font-semibold">{rushUsageLabel}</p>}
                {rushResetLabel && <p className="text-xs opacity-80">{rushResetLabel}</p>}
              </div>
            </div>
          </div>
        )}
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center space-x-4">
            <Link
              to="/dashboard"
              className="flex items-center text-gray-600 dark:text-gray-300 hover:text-subject-600 dark:hover:text-subject-400 transition-colors"
            >
              <ArrowLeftIcon className="h-5 w-5 mr-1" />
              Back to Dashboard
            </Link>
            <div className="w-px h-6 bg-gray-300 dark:bg-gray-600"></div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              Rush Mode
            </h1>
          </div>
          
          {/* Game Stats */}
          <div className="flex items-center space-x-6">
            <div className="flex items-center space-x-2">
              <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Question:</span>
              <span className="text-lg font-bold text-gray-900 dark:text-white">
                {currentQuestionNumber}
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <ClockIcon className="h-5 w-5 text-gray-400" />
              <span className={`text-lg font-bold ${getTimeColor()}`}>
                {formatTime(timeLeft)}
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Lives:</span>
              <div className="flex space-x-1">
                {renderLives()}
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Score:</span>
              <span className="text-lg font-bold text-subject-600 dark:text-subject-400">
                {score}
              </span>
            </div>
          </div>
        </div>

        {/* Question Card */}
        {question && (
          <div className="mb-8">
            <QuestionCard
              question={question}
              onAnswer={handleAnswer}
              isSubmitting={submittingAnswer}
              showAnswer={!!lastAnswerResult}
            />

            {/* Result Feedback */}
            {lastAnswerResult && (
              <div className={`mt-4 p-4 rounded-lg text-center ${
                lastAnswerResult.isCorrect
                  ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800'
                  : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
              }`}>
                <p className={`text-lg font-semibold mb-2 ${
                  lastAnswerResult.isCorrect
                    ? 'text-green-800 dark:text-green-200'
                    : 'text-red-800 dark:text-red-200'
                }`}>
                  {lastAnswerResult.isCorrect ? '🎉 +1 Point!' : '❌ Lost a life!'}
                </p>
                {!lastAnswerResult.isCorrect && (
                  <>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                      You selected: <span className="font-semibold">{lastAnswerResult.selected}</span>
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                      Correct answer: <span className="font-semibold">{lastAnswerResult.correctAnswer}</span>
                    </p>
                    <p className="text-red-600 dark:text-red-300">
                      Keep going! You have {lives} {lives === 1 ? 'life' : 'lives'} remaining.
                    </p>
                  </>
                )}
              </div>
            )}
          </div>
        )}

        {/* Progress Panel */}
        <GameProgressPanel
          attemptHistory={attemptHistory}
          currentQuestionNumber={currentQuestionNumber}
          gameMode="rush"
          lives={lives}
          maxLives={3}
          score={score}
          timeRemaining={timeLeft}
          totalTime={selectedDuration ? selectedDuration * 60 * 1000 : undefined}
        />

        <FreeTierLimitModal
          isOpen={showLimitModal}
          onClose={() => setShowLimitModal(false)}
          feature="rush"
          usage={subscriptionStatus?.free_tier_usage}
        />
      </div>
    </div>
  );
};

export default Rush;
